import React, { useCallback, useMemo } from "react";

import { Platform, View, ViewStyle } from "react-native";

import {
  AHText,
  useTheme,
  useLayoutContext,
  Box,
  TouchableBox,
  SignedActor,
} from "arkh";

import { TaskExecutionHeader } from "./TaskExecutionHeader";

import { TaskExecutionComponent } from "./TaskExecutionComponents/TaskExecutionComponent";

import Animated, {
  useAnimatedStyle,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import { FormattedMessage } from "react-intl";
import { graphql, useFragment } from "react-relay";
import { TaskExecutionListItemComponent_taskExecution$key } from "./__generated__/TaskExecutionListItemComponent_taskExecution.graphql";
import { TaskExecutionProps } from "./TaskExecutionComponents/TaskExecutionProps";

import { TaskExecutionListItemUpdatedInfo_taskExecution$key } from "./__generated__/TaskExecutionListItemUpdatedInfo_taskExecution.graphql";
import { StoreFormattedDate } from "../../../common/dates/StoreFormattedDate";

function CommitedBarCmp(props: { isCommittable: boolean }) {
  const theme = useTheme();
  const layout = useLayoutContext();

  return (
    <View
      style={{
        height: "100%",
        width: layout.space.medium,
        position: "absolute",
        top: 0,
        bottom: 0,
        right: 0,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <View
        style={{
          opacity: props.isCommittable ? 1 : 0,
          backgroundColor: theme.positive,
          height: "80%",
          maxHeight: 100,
          width: 6,
          borderRadius: 3,
        }}
      />
    </View>
  );
}
function AnimatedCommittedBarCmp(props: { isCommittable: boolean }) {
  const theme = useTheme();
  const layout = useLayoutContext();

  const style = useAnimatedStyle(() => ({
    transform: [
      {
        translateX: withSpring(props.isCommittable ? 0 : layout.space.medium, {
          velocity: 200,
        }),
      },
    ],
    opacity: withTiming(props.isCommittable ? 1 : 0),
    backgroundColor: theme.positive,
    height: "80%",
    maxHeight: 100,
    width: 6,
    borderRadius: 3,
  }));

  return (
    <View
      style={{
        height: "100%",
        width: layout.space.medium,
        position: "absolute",
        top: 0,
        bottom: 0,
        right: 0,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Animated.View style={style} />
    </View>
  );
}
const Bar = React.memo(
  Platform.select({
    android: CommitedBarCmp,
    default: AnimatedCommittedBarCmp,
  })
);

const Wrapper: React.FC<
  React.PropsWithChildren<{
    isCommittable: boolean;
    style?: ViewStyle;
  }>
> = (props) => {
  const { space } = useLayoutContext();

  const viewStyle: ViewStyle = useMemo(
    () => ({
      marginHorizontal: space.medium,
    }),
    [space.medium]
  );
  return (
    <Box style={props.style}>
      <Bar isCommittable={props.isCommittable} />
      <Box cornerRadius="medium" color="surface-primary" style={viewStyle}>
        {props.children}
      </Box>
    </Box>
  );
};

export type TaskExecutionListItemProps = TaskExecutionProps & {
  disabled?: boolean;
  style?: ViewStyle;
  createIssue: (taskExecutionId: string, taskTitle: string) => void;
  taskExecution: TaskExecutionListItemComponent_taskExecution$key;
};

const TaskExecutionCmp = (props: TaskExecutionListItemProps) => {
  const taskExecutionListItem = useFragment(
    graphql`
      fragment TaskExecutionListItemComponent_taskExecution on TaskExecution {
        isCompleted
        id
        task {
          id
          title
          ...TaskExecutionHeaderComponent_Task
        }

        ...TaskExecutionListItemUpdatedInfo_taskExecution
        ...TaskExecutionComponent_TaskExecution
      }
    `,
    props.taskExecution
  );

  return (
    <Wrapper
      style={props.style}
      isCommittable={taskExecutionListItem.isCompleted ?? false}
    >
      <Box space="medium" spaceBottom="none" style={{ flexDirection: "row" }}>
        <View style={{ flex: 1 }}>
          <View style={{ flexDirection: "column" }}>
            <TaskExecutionHeader
              openInstructions={props.openInstructions}
              task={taskExecutionListItem.task}
            />
          </View>
          <TaskExecutionComponent
            {...props}
            taskExecution={taskExecutionListItem}
          />
        </View>
      </Box>
      <Box
        spaceBottom="medium"
        spaceTop="small"
        spaceHorizontal="medium"
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <UpdatedInfo taskExecution={taskExecutionListItem} />
        {props.disabled ? null : (
          <ReportIssueButton
            taskExecutionId={taskExecutionListItem.id}
            taskTitle={taskExecutionListItem.task.title}
            createIssue={props.createIssue}
          />
        )}
      </Box>
    </Wrapper>
  );
};

function ReportIssueButtonCMP(props: {
  taskExecutionId: string;
  taskTitle: string;
  createIssue: (taskExecutionId: string, taskTitle: string) => void;
}) {
  const onAddIssue = useCallback(() => {
    props.createIssue(props.taskExecutionId, props.taskTitle);
  }, [props]);

  return (
    <TouchableBox
      disabled={false}
      space="small"
      cornerRadius="medium"
      style={{
        borderRadius: 15,
      }}
      color="background"
      hitSlop={{ left: 0, bottom: 20, right: 20 }}
      onPress={onAddIssue}
    >
      <AHText type="info-small" color="washedText" size={10}>
        <FormattedMessage
          defaultMessage="Report issue"
          description="TaskExecutionListItem: Issue btn"
        />
      </AHText>
    </TouchableBox>
  );
}
const ReportIssueButton = React.memo(ReportIssueButtonCMP);

export const TaskExecution = React.memo(TaskExecutionCmp);

function UpdatedInfo(props: {
  taskExecution: TaskExecutionListItemUpdatedInfo_taskExecution$key;
}) {
  const taskExecution = useFragment(
    graphql`
      fragment TaskExecutionListItemUpdatedInfo_taskExecution on TaskExecution {
        updatedAtDate {
          ...StoreFormattedDate
        }
        isCompleted

        lastUpdatedByActor {
          ...SignedActor_actor
        }
      }
    `,
    props.taskExecution
  );

  return (
    <Box
      spaceBottom="xxsmall"
      spaceRight="medium"
      style={{ flex: 1, flexDirection: "row", alignItems: "center" }}
    >
      {taskExecution.isCompleted && taskExecution.lastUpdatedByActor ? (
        <>
          <SignedActor signedActor={taskExecution.lastUpdatedByActor} />
          {taskExecution.updatedAtDate ? (
            <AHText spaceLeft="small" type="detail" color="text-secondary">
              <StoreFormattedDate date={taskExecution.updatedAtDate} />
            </AHText>
          ) : null}
        </>
      ) : null}
    </Box>
  );
}
